// GameOverOverlay — the "Game Over" card that appears when a sport's score
// threshold is reached (or, for baseball, when 10 pitches have been thrown).
// Shows the final YOU vs CPU tally, declares a winner, and offers two
// actions: Play Again (resets the arena scores) and Change Sport (closes).
//
// The overlay is data-driven from `arenaApi.scores` and the `derbyDone`
// signal passed in via prop; other sports use the score threshold below.

import React, { useEffect, useMemo, useState } from "react";
import { Trophy, RotateCcw, ChevronRight } from "lucide-react";

import { COLORS } from "../constants";

// Score threshold that ends a round (basketball / soccer / football).
// First side to hit `TARGET_SCORE` wins the session; baseball uses a fixed
// 10-pitch cap driven by SportsArena.
const TARGET_SCORE = { basketball: 5, soccer: 3, football: 3 };

export default function GameOverOverlay({ arenaApi, derbyDone }) {
  const { sport, scores, resetScores, setSport } = arenaApi;
  const [dismissed, setDismissed] = useState(false);

  const winner = useMemo(() => {
    if (sport === "baseball") {
      if (!derbyDone) return null;
      if (scores.player > scores.cpu) return "player";
      if (scores.cpu > scores.player) return "cpu";
      return "tie";
    }
    const target = TARGET_SCORE[sport] || 5;
    if (scores.player >= target) return "player";
    if (scores.cpu >= target) return "cpu";
    return null;
  }, [sport, scores, derbyDone]);

  // Re-arm the overlay whenever a new game is started (either by resetting
  // or switching sport) — dismissal is per-round.
  useEffect(() => {
    setDismissed(false);
  }, [sport]);

  useEffect(() => {
    // If scores go back to 0-0 (a reset happened) re-arm.
    if (scores.player === 0 && scores.cpu === 0) setDismissed(false);
  }, [scores.player, scores.cpu]);

  if (!winner || dismissed) return null;

  const scoreLabel =
    sport === "baseball" ? "HR" : sport === "football" ? "TD" : sport === "soccer" ? "GOALS" : "PTS";

  const line =
    winner === "player"
      ? "You win."
      : winner === "cpu"
      ? "CPU wins."
      : "It's a tie.";

  const handlePlayAgain = () => {
    if (typeof resetScores === "function") resetScores();
    setDismissed(true);
  };

  const handleChangeSport = () => {
    setDismissed(true);
    // Nudge sport to next in the ring so the visitor sees an obvious change.
    const order = ["basketball", "soccer", "football", "baseball"];
    const idx = order.indexOf(sport);
    const next = order[(idx + 1) % order.length];
    if (typeof setSport === "function") setSport(next);
    if (typeof resetScores === "function") resetScores();
  };

  return (
    <div
      data-testid="gameover-overlay"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 88,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(11,21,48,0.55)",
        backdropFilter: "blur(4px)",
        padding: 20,
      }}
    >
      <div
        style={{
          position: "relative",
          maxWidth: 440,
          width: "100%",
          padding: "30px 28px 24px",
          borderRadius: 18,
          background:
            "linear-gradient(180deg, rgba(11,21,48,0.96) 0%, rgba(6,14,32,0.96) 100%)",
          border: `1px solid ${COLORS.gold}`,
          boxShadow: "0 24px 56px rgba(0,0,0,0.5), 0 0 0 6px rgba(230,180,80,0.08)",
          textAlign: "center",
          color: "#f3ead4",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            fontSize: 11,
            letterSpacing: 2,
            color: "#c9b382",
            fontWeight: 700,
            textTransform: "uppercase",
            marginBottom: 6,
          }}
        >
          <Trophy size={14} />
          {sport === "baseball" ? "Derby Complete" : "Final Whistle"}
        </div>
        <h2
          style={{
            margin: 0,
            fontFamily: "'Cormorant Garamond','Playfair Display',serif",
            fontSize: 40,
            fontWeight: 600,
            lineHeight: 1.05,
            color: winner === "player" ? "#f7dc95" : winner === "cpu" ? "#e17a7a" : "#dcd3b8",
          }}
        >
          {line}
        </h2>

        <div
          data-testid="gameover-scoreline"
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "center",
            gap: 24,
            margin: "22px 0 14px",
          }}
        >
          <ScoreCol label="YOU" value={scores.player} highlight={winner === "player"} />
          <div style={{ opacity: 0.55, fontSize: 18, letterSpacing: 3 }}>vs</div>
          <ScoreCol label="CPU" value={scores.cpu} highlight={winner === "cpu"} />
        </div>
        <div style={{ fontSize: 12, letterSpacing: 2, color: "#c9b382", opacity: 0.8 }}>
          {scoreLabel}
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 22 }}>
          <button
            data-testid="gameover-play-again"
            onClick={handlePlayAgain}
            style={btnGold}
          >
            <RotateCcw size={14} />
            Play Again
          </button>
          <button
            data-testid="gameover-change-sport"
            onClick={handleChangeSport}
            style={btnGhost}
          >
            <ChevronRight size={14} />
            Next Sport
          </button>
        </div>
      </div>
    </div>
  );
}

function ScoreCol({ label, value, highlight }) {
  return (
    <div style={{ minWidth: 90 }}>
      <div
        style={{
          fontSize: 68,
          fontWeight: 700,
          fontFamily: "'Cormorant Garamond','Playfair Display',serif",
          lineHeight: 1,
          color: highlight ? "#f7dc95" : "#dcd3b8",
        }}
      >
        {value}
      </div>
      <div
        style={{
          marginTop: 4,
          fontSize: 11,
          letterSpacing: 2,
          color: highlight ? "#f7dc95" : "#c9b382",
          opacity: 0.85,
          fontWeight: 700,
        }}
      >
        {label}
      </div>
    </div>
  );
}

const btnGold = {
  flex: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "11px 12px",
  borderRadius: 12,
  border: `1px solid ${COLORS.gold}`,
  background: "linear-gradient(180deg,#f7dc95 0%,#e6b450 50%,#c8933a 100%)",
  color: "#111827",
  fontFamily: "'Cormorant Garamond','Playfair Display',serif",
  fontWeight: 700,
  fontSize: 14,
  letterSpacing: 1.4,
  textTransform: "uppercase",
  cursor: "pointer",
};

const btnGhost = {
  flex: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "11px 12px",
  borderRadius: 12,
  background: "transparent",
  border: "1px solid rgba(230,180,80,0.32)",
  color: "#c9b382",
  fontSize: 12,
  letterSpacing: 1.4,
  textTransform: "uppercase",
  cursor: "pointer",
};
